import { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import OutsideClickHandler from 'react-outside-click-handler';

import Icon from '@components/icon';

import { AppleIcon, GoogleIcon, Microsoft365Icon, OutlookIcon } from './provider-icons';
import {
  container,
  trigger_button,
  dropdown,
  dropdown_inner,
  option,
  modal_overlay,
  modal,
  modal_icon,
  modal_title,
  modal_subtitle,
  date_card,
  date_card_badge,
  date_card_badge_day,
  date_card_badge_month,
  date_card_details,
  date_card_name,
  date_card_info,
  date_card_name_hover,
  modal_close
} from './styles.module.scss';

const formatDateCompact = (date, time) =>
  date.replace(/-/g, '') + 'T' + time.replace(':', '') + '00';

const generateGoogleUrl = ({
  name,
  description,
  location,
  startDate,
  startTime,
  endDate,
  endTime,
  timeZone
}) => {
  const start = formatDateCompact(startDate, startTime);
  const end = formatDateCompact(endDate, endTime);
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: name,
    dates: `${start}/${end}`,
    details: description,
    location,
    ctz: timeZone
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

const generateOutlookUrl = (
  base,
  { name, description, location, startDate, startTime, endDate, endTime }
) => {
  const params = new URLSearchParams({
    subject: name,
    startdt: `${startDate}T${startTime}:00`,
    enddt: `${endDate}T${endTime}:00`,
    body: description,
    location,
    path: '/calendar/action/compose'
  });
  return `${base}?${params.toString()}`;
};

const generateIcsEvent = ({
  name,
  description,
  location,
  startDate,
  startTime,
  endDate,
  endTime,
  timeZone
}) => {
  const start = formatDateCompact(startDate, startTime);
  const end = formatDateCompact(endDate, endTime);
  const escapedDesc = description.replace(/\n/g, '\\n');

  return [
    'BEGIN:VEVENT',
    `DTSTART;TZID=${timeZone}:${start}`,
    `DTEND;TZID=${timeZone}:${end}`,
    `SUMMARY:${name}`,
    `DESCRIPTION:${escapedDesc}`,
    `LOCATION:${location}`,
    'END:VEVENT'
  ].join('\r\n');
};

const generateIcsContent = (events) => {
  const icsEvents = events.map((event) => generateIcsEvent(event)).join('\r\n');

  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Sibcy Cline//Open House//EN',
    icsEvents,
    'END:VCALENDAR'
  ].join('\r\n');
};

const downloadIcs = (events) => {
  const blob = new Blob([generateIcsContent(events)], {
    type: 'text/calendar;charset=utf-8'
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'open-house.ics';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const PROVIDERS = [
  { key: 'apple', label: 'Apple', icon: AppleIcon },
  { key: 'google', label: 'Google', icon: GoogleIcon },
  { key: 'microsoft365', label: 'Microsoft 365', icon: Microsoft365Icon },
  { key: 'outlook', label: 'Outlook.com', icon: OutlookIcon }
];

const formatTime = (date) => {
  const d = new Date(`${date.startDate}T${date.startTime}`);
  const e = new Date(`${date.endDate}T${date.endTime}`);
  const fmt = { hour: 'numeric', minute: '2-digit' };
  return `${d.toLocaleTimeString('en-US', fmt)} - ${e.toLocaleTimeString('en-US', fmt)}`;
};

const getDateParts = (date) => {
  const d = new Date(`${date.startDate}T${date.startTime}`);
  return {
    day: d.getDate(),
    month: d.toLocaleDateString('en-US', { month: 'short' }),
    fullDate: d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })
  };
};

const AddToCalendar = ({ label, name, description, location, dates, timeZone }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const hasMultipleDates = dates.length > 1;

  useEffect(() => {
    if (selectedProvider) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [selectedProvider]);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleToggle = useCallback(() => {
    if (isOpen || selectedProvider) {
      setIsOpen(false);
      setSelectedProvider(null);
    } else {
      setIsOpen(true);
    }
  }, [isOpen, selectedProvider]);

  const buildEvent = useCallback(
    (date) => ({ name, description, location, timeZone, ...date }),
    [name, description, location, timeZone]
  );

  const openForProvider = useCallback(
    (providerKey, date) => {
      const event = buildEvent(date);

      switch (providerKey) {
        case 'google':
          window.open(generateGoogleUrl(event), '_blank');
          break;
        case 'outlook':
          window.open(
            generateOutlookUrl('https://outlook.live.com/calendar/0/action/compose', event),
            '_blank'
          );
          break;
        case 'microsoft365':
          window.open(
            generateOutlookUrl('https://outlook.office.com/calendar/0/action/compose', event),
            '_blank'
          );
          break;
      }
    },
    [buildEvent]
  );

  const handleProviderClick = useCallback(
    (providerKey) => {
      if (providerKey === 'apple') {
        downloadIcs(dates.map(buildEvent));
        close();
        return;
      }

      if (!hasMultipleDates) {
        openForProvider(providerKey, dates[0]);
        close();
        return;
      }

      setSelectedProvider(providerKey);
      setIsOpen(false);
    },
    [dates, hasMultipleDates, buildEvent, openForProvider, close]
  );

  const handleDateSelect = useCallback(
    (index) => {
      openForProvider(selectedProvider, dates[index]);
      setSelectedProvider(null);
    },
    [selectedProvider, dates, openForProvider]
  );

  const closeModal = useCallback(() => {
    setSelectedProvider(null);
  }, []);

  return (
    <>
      <OutsideClickHandler onOutsideClick={close}>
        <div className={container}>
          <button
            className={trigger_button}
            onClick={handleToggle}
            aria-expanded={isOpen}
            type="button"
          >
            <Icon name="calendar" type="general" />
            {label}
          </button>

          {isOpen && (
            <div className={dropdown}>
              <div className={dropdown_inner}>
                {PROVIDERS.map((provider) => {
                  const ProviderIcon = provider.icon;
                  return (
                    <button
                      key={provider.key}
                      className={option}
                      onClick={() => handleProviderClick(provider.key)}
                      type="button"
                    >
                      <ProviderIcon />
                      {provider.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </OutsideClickHandler>

      {/* eslint-disable jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      {selectedProvider && (
        <div className={modal_overlay} onClick={closeModal}>
          <div className={modal} onClick={(e) => e.stopPropagation()}>
            <div className={modal_icon}>
              {(() => {
                const provider = PROVIDERS.find((p) => p.key === selectedProvider);
                const ProviderIcon = provider?.icon;
                return ProviderIcon ? <ProviderIcon /> : null;
              })()}
            </div>
            <h3 className={modal_title}>This is an event series</h3>
            <p className={modal_subtitle}>Add the individual events one by one:</p>

            {dates.map((date, index) => {
              const { day, month, fullDate } = getDateParts(date);
              return (
                <button
                  key={index}
                  className={date_card}
                  onClick={() => handleDateSelect(index)}
                  type="button"
                >
                  <div className={date_card_badge}>
                    <span className={date_card_badge_day}>{day}</span>
                    <span className={date_card_badge_month}>{month}</span>
                  </div>
                  <div className={date_card_details}>
                    <span className={date_card_name}>{name}</span>
                    <span className={date_card_name_hover}>Add to Calendar</span>
                    <span className={date_card_info}>{location}</span>
                    <span className={date_card_info}>
                      {fullDate}, {formatTime(date)}
                    </span>
                  </div>
                </button>
              );
            })}

            <button className={modal_close} onClick={closeModal} type="button">
              Close
            </button>
          </div>
        </div>
      )}
      {/* eslint-enable jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
    </>
  );
};

AddToCalendar.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  location: PropTypes.string.isRequired,
  dates: PropTypes.arrayOf(
    PropTypes.shape({
      startDate: PropTypes.string.isRequired,
      endDate: PropTypes.string.isRequired,
      startTime: PropTypes.string.isRequired,
      endTime: PropTypes.string.isRequired
    })
  ).isRequired,
  timeZone: PropTypes.string.isRequired
};

export default AddToCalendar;
